// Current-sensor (phase meter) data model — DeviceID 82 single-phase and 16
// three-phase. Ported from phaseScreenFunctions.dart + the two Flutter screens.
//
// One call per DAY: GetCurrentSenseByUniqueNumberAndDateOnly returns rows of
// { DateTime, current, power, phase } roughly every 30–60 s.
//
// ⚠️ The date string is NOT zero-padded — Flutter sends `${y}-${m}-${d}`, e.g.
// "2026-7-6". Sending "2026-07-06" is a different string to this backend, so
// keep buildDateParam() as the single place that formats it.
//
// Charting note: Flutter plots every series against the ROW INDEX, not against
// time. For three-phase that means the R/Y/B lines occupy interleaved indices
// rather than sharing an x position. toChartSeries() reproduces exactly that —
// one row per index, with only the matching phase's key filled in — so the
// React chart draws the same picture as the Flutter one.

import { getCurrentSense } from '../api/api';

export const PHASE_COLORS = {
  1: '#F44336', // Colors.red
  2: '#4CAF50', // Colors.green
  3: '#2196F3', // Colors.blue
};

export const PHASE_LABELS = { 1: 'Phase 1', 2: 'Phase 2', 3: 'Phase 3' };

// ₹ per kWh — the backend's convention for the bill estimate.
export const RATE = 15;

export function buildDateParam(date) {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

// Readings carry naive local timestamps. Drop a trailing 'Z' before parsing, or
// the browser shifts every reading into UTC and the graph no longer lines up
// with the local From/To the user typed.
export function parseLocal(s) {
  return new Date(String(s).replace('Z', '').replace('T', ' '));
}

function hhmm(iso) {
  const d = parseLocal(iso);
  if (Number.isNaN(+d)) return '';
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// rows -> [{ i, t, p1, p2, p3 }] for `dataKey` ('power' | 'current').
export function toChartSeries(rows, dataKey) {
  return rows.map((row, i) => {
    const phase = Number(row.phase) || 1;
    const value = Number.parseFloat(String(row[dataKey])) || 0;
    return { i, t: hhmm(row.DateTime), [`p${phase}`]: value };
  });
}

// Which phases actually appear in the data (so a "three phase" meter wired for
// one still draws one line rather than three empty ones).
export function phasesPresent(rows) {
  const set = new Set(rows.map((r) => Number(r.phase) || 1));
  return [...set].sort((a, b) => a - b);
}

// Left-axis tick text: power is shown in k above 1000, current as a plain int.
export function axisTick(value, dataKey) {
  if (dataKey === 'power' && value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return String(Math.trunc(value));
}

// Grid spacing, straight from the Dart: 1000 for power, 1 for current.
export function gridInterval(dataKey) {
  return dataKey === 'power' ? 1000 : 1;
}

// Explicit y-axis ticks on the Dart's interval (1000 / 1), from 0 to the peak.
//
// Flutter states the interval and fl_chart draws a line at every multiple; the
// React chart takes a tick LIST instead, so build the same multiples here. The
// count is capped because a meter reading 40 A would otherwise ask for 40 ticks
// on a phone-height graph — past the cap the interval is scaled up to the next
// multiple that fits, which keeps the labels on the Dart's grid rather than
// landing on arbitrary values.
export function yTicks(rows, dataKey, maxTicks = 8) {
  let peak = 0;
  rows.forEach((r) => {
    const v = Number.parseFloat(String(r[dataKey])) || 0;
    if (v > peak) peak = v;
  });
  if (peak <= 0) return [0];

  const base = gridInterval(dataKey);
  let step = base;
  while (peak / step > maxTicks) step += base;

  const ticks = [];
  for (let v = 0; v <= peak + step; v += step) ticks.push(v);
  return ticks;
}

// How many rows to skip between x-axis labels. Flutter uses length/2.5 on the
// single-phase screen and length/3 on the three-phase one.
export function xTickStep(length, threePhase) {
  const step = Math.floor(length / (threePhase ? 3 : 2.5));
  return Math.max(1, step || 1);
}

// ── Range loading + consumption ────────────────────────────────────────────
// The endpoint only answers PER DAY, so a From/To range is one request per day
// in it, cached and merged. Shared by the desktop card and the mobile screen so
// both report the same kWh for the same range.

const dayCache = new Map(); // "uno|YYYY-M-D" -> { rows, at }

// Past days never change, so they cache for hours; today keeps a short TTL so
// new readings still appear.
export async function fetchDay(uniqueNumber, dateParam, isToday) {
  const key = `${uniqueNumber}|${dateParam}`;
  const hit = dayCache.get(key);
  const ttl = isToday ? 30000 : 6 * 3600000;
  if (hit && Date.now() - hit.at < ttl) return hit.rows;
  const res = await getCurrentSense(uniqueNumber, dateParam).catch(() => null);
  const rows = res && Array.isArray(res.data) ? res.data : [];
  dayCache.set(key, { rows, at: Date.now() });
  return rows;
}

export function clearPhaseCache() {
  dayCache.clear();
}

// Every reading in [from, to], ascending. Capped at 62 days so a mistyped year
// can't fire hundreds of requests.
export async function loadRange(uniqueNumber, from, to) {
  if (Number.isNaN(+from) || Number.isNaN(+to) || to <= from) return [];

  const days = [];
  const cur = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  while (cur <= end && days.length < 62) {
    days.push(buildDateParam(cur));
    cur.setDate(cur.getDate() + 1);
  }

  const todayParam = buildDateParam(new Date());
  const results = await Promise.all(
    days.map((d) => fetchDay(uniqueNumber, d, d === todayParam)),
  );

  return results
    .flat()
    .filter((r) => {
      const t = parseLocal(r.DateTime);
      return t >= from && t <= to;
    })
    .sort((a, b) => parseLocal(a.DateTime) - parseLocal(b.DateTime));
}

// Thin a series so a month-long range doesn't hand the chart 50k points.
export function downsample(rows, max = 160) {
  if (rows.length <= max) return rows;
  const step = Math.ceil(rows.length / max);
  return rows.filter((_, i) => i % step === 0);
}

// Energy (kWh) = Σ power × interval, summed PER PHASE then across phases.
//
// Readings are ~30–60 s apart. A gap longer than 2 minutes means the device was
// offline, not that it drew that power the whole time — so such gaps are charged
// at 1 minute instead of their real length, which stops an outage inflating the
// bill.
export function energyKWh(rows) {
  const byPhase = {};
  rows.forEach((r) => {
    const p = r.phase;
    (byPhase[p] = byPhase[p] || []).push(r);
  });

  let wh = 0;
  Object.values(byPhase).forEach((list) => {
    for (let i = 0; i < list.length - 1; i += 1) {
      let h = (parseLocal(list[i + 1].DateTime) - parseLocal(list[i].DateTime)) / 3600000;
      if (h <= 0) continue; // eslint-disable-line no-continue
      if (h > 2 / 60) h = 1 / 60;
      wh += (Number(list[i].power) || 0) * h;
    }
  });
  return wh / 1000;
}

// { kwh, wh, bill } for a set of readings.
export function consumptionOf(rows) {
  const kwh = energyKWh(rows);
  return { kwh, wh: kwh * 1000, bill: kwh * RATE };
}

// ── datetime-local helpers ─────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, '0');

export function toLocalInput(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function startOfToday() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0);
}

export function endOfToday() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59);
}
